/**
 * Carga de dados com os três estados sempre explícitos.
 *
 * Tela sem estado de erro engana: um fetch que falha e some deixa a página
 * parecendo "vazia", e vazio e quebrado são conclusões operacionais opostas.
 * Por isso o hook devolve `Carga<T>` e a tela é obrigada a tratar os três.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { Carga } from "../api.js";
import { Aviso, Botao } from "./base.js";

export function useCarga<T>(
  buscar: () => Promise<T>,
  chave: string,
): { carga: Carga<T>; recarregar: () => void } {
  // A busca muda de identidade a cada render; guardar em ref deixa o efeito
  // depender só da CHAVE (os filtros), não da closure recriada.
  const ref = useRef(buscar);
  ref.current = buscar;

  const [carga, setCarga] = useState<Carga<T>>({ fase: "carregando" });
  const [ciclo, setCiclo] = useState(0);

  useEffect(() => {
    let vivo = true;
    setCarga({ fase: "carregando" });
    ref
      .current()
      .then((dados) => {
        if (vivo) setCarga({ fase: "pronto", dados });
      })
      .catch((e: unknown) => {
        if (!vivo) return;
        setCarga({
          fase: "erro",
          mensagem: e instanceof Error ? e.message : "Falha ao carregar.",
        });
      });
    return () => {
      vivo = false;
    };
  }, [chave, ciclo]);

  return { carga, recarregar: useCallback(() => setCiclo((c) => c + 1), []) };
}

export function ErroTela({
  mensagem,
  aoTentar,
}: {
  mensagem: string;
  aoTentar?: () => void;
}): JSX.Element {
  return (
    <div role="alert">
      <Aviso tom="crit">
        <div className="flex items-start gap-3 flex-wrap">
          <span className="flex-1 min-w-[220px]">{mensagem}</span>
          {aoTentar && (
            <Botao pequeno onClick={aoTentar}>
              Tentar de novo
            </Botao>
          )}
        </div>
      </Aviso>
    </div>
  );
}

/** Faixa de erro para ação pontual (liberar, cancelar, salvar). */
export function ErroAcao({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div role="alert">
      <Aviso tom="crit">{children}</Aviso>
    </div>
  );
}
